import {
  EMPLOYER_CHANGE_RESTRICTION_PERIOD_LABEL,
  EXAM_GRADE_1YEAR_LABEL,
  EXAM_GRADE_FINAL_LABEL,
  TEmployerChangeRestrictionPeriod,
  TExamGrade1Year,
  TExamGradeFinal,
  TMasterCompanyMainSkill,
} from "@/api/entities/master-company-main-skill.entity";
import { createCompanyMainSkillApi, updateCompanyMainSkillApi } from "@/api/services/global/master";
import { ConfirmSaveModal } from "@/components/commons/ConfirmSaveModal";
import { Button } from "@/components/elements/Button";
import { Form } from "@/components/elements/Form";
import { FormField } from "@/components/elements/FormField";
import { Input } from "@/components/elements/Input";
import { Modal } from "@/components/elements/Modal";
import { Select } from "@/components/elements/Select";
import { useDialog } from "@/hooks/useDialog";
import { API_SUCCESS_MESSAGE } from "@/constants/common";
import { QUERY_KEYS } from "@/constants/queryKeys";
import { useConfirmCloseModal } from "@/features/master-data/hooks/useConfirmCloseModal";
import { companyMainSkillSchema } from "@/features/master-data/schema/masterDataCompanySpecificIndustrySchema";
import { TCompanyMainSkillForm } from "@/features/master-data/types/masterDataCompanySpecificIndustryForm";
import { TOption } from "@/types/common";
import { setErrorForm, showError } from "@/utils/error";
import { toNumberOrNull } from "@/utils/number";
import { toast } from "@/utils/toast";
import { yupResolver } from "@hookform/resolvers/yup";
import { Grid, Stack, Typography } from "@mui/material";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { FC, useEffect, useMemo } from "react";
import { Resolver, useForm } from "react-hook-form";

const EXAM_GRADE_1YEAR_OPTIONS: TOption[] = Object.entries(EXAM_GRADE_1YEAR_LABEL).map(([code, label]) => ({
  label,
  value: Number(code),
}));
const EXAM_GRADE_FINAL_OPTIONS: TOption[] = Object.entries(EXAM_GRADE_FINAL_LABEL).map(([code, label]) => ({
  label,
  value: Number(code),
}));
const EMPLOYER_CHANGE_RESTRICTION_PERIOD_OPTIONS: TOption[] = Object.entries(EMPLOYER_CHANGE_RESTRICTION_PERIOD_LABEL).map(([code, label]) => ({
  label,
  value: Number(code),
}));

type TCompanyMainSkillFormModalProps = {
  onClose: () => void;
  isOpen: boolean;
  selectedCompanyMainSkill?: TMasterCompanyMainSkill;
  existingCompanySpecificIndustryId: number;
  existingCompanySpecificBusinessClassificationId: number;
  existingCompanySpecificBusinessClassificationName: string;
  setCompanyMainSkillRows: React.Dispatch<React.SetStateAction<TMasterCompanyMainSkill[]>>;
};

export const CompanyMainSkillFormModal: FC<TCompanyMainSkillFormModalProps> = ({
  onClose,
  isOpen,
  selectedCompanyMainSkill,
  existingCompanySpecificIndustryId,
  existingCompanySpecificBusinessClassificationId,
  existingCompanySpecificBusinessClassificationName,
  setCompanyMainSkillRows,
}) => {
  const queryClient = useQueryClient();

  // Select must stay controlled, so 級 is "" here and converted back to null in the payload.
  const defaultValues = useMemo<TCompanyMainSkillForm>(
    () => ({
      mainSkillName: selectedCompanyMainSkill?.mainSkillName ?? "",
      examName1Year: selectedCompanyMainSkill?.examName1Year ?? "",
      examGrade1Year: selectedCompanyMainSkill?.examGrade1Year ?? "",
      examNameFinal: selectedCompanyMainSkill?.examNameFinal ?? "",
      examGradeFinal: selectedCompanyMainSkill?.examGradeFinal ?? "",
      employerChangeRestrictionPeriod: selectedCompanyMainSkill?.employerChangeRestrictionPeriod ?? "",
    }),
    [selectedCompanyMainSkill],
  );

  const methods = useForm<TCompanyMainSkillForm>({
    resolver: yupResolver(companyMainSkillSchema) as Resolver<TCompanyMainSkillForm>,
    mode: "onBlur",
    defaultValues,
  });

  const {
    control,
    reset,
    formState: { isDirty },
  } = methods;
  const { confirmClose, handleRequestClose } = useConfirmCloseModal(isDirty, onClose);

  // useForm only reads defaultValues on mount, so re-apply them whenever the selected row changes.
  useEffect(() => {
    reset(defaultValues);
  }, [defaultValues]);

  const { mutate: saveCompanyMainSkill, isPending } = useMutation({
    mutationFn: (data: TCompanyMainSkillForm) => {
      const payload = {
        mainSkillName: data.mainSkillName,
        examName1Year: data.examName1Year,
        examGrade1Year: (data.examGrade1Year === "" ? null : data.examGrade1Year) as TExamGrade1Year | null,
        examNameFinal: data.examNameFinal,
        examGradeFinal: (data.examGradeFinal === "" ? null : data.examGradeFinal) as TExamGradeFinal | null,
        employerChangeRestrictionPeriod: (data.employerChangeRestrictionPeriod === ""
          ? null
          : data.employerChangeRestrictionPeriod) as TEmployerChangeRestrictionPeriod | null,
      };

      const companySpecificIndustryId = toNumberOrNull(existingCompanySpecificIndustryId);
      const companySpecificBusinessClassificationId = toNumberOrNull(existingCompanySpecificBusinessClassificationId);
      if (companySpecificIndustryId === null || companySpecificBusinessClassificationId === null) {
        return Promise.reject(new Error("companySpecificIndustryId / companySpecificBusinessClassificationId is required"));
      }

      const mainSkillId = toNumberOrNull(selectedCompanyMainSkill?.id);
      if (mainSkillId !== null) {
        return updateCompanyMainSkillApi(companySpecificIndustryId, companySpecificBusinessClassificationId, mainSkillId, payload);
      }
      return createCompanyMainSkillApi(companySpecificIndustryId, companySpecificBusinessClassificationId, payload);
    },
    onSuccess: (rs) => {
      const savedId = toNumberOrNull(rs.data.id);
      setCompanyMainSkillRows((prev) =>
        selectedCompanyMainSkill
          ? prev.map((item) => (savedId !== null && toNumberOrNull(item.id) === savedId ? rs.data : item))
          : [...prev, rs.data],
      );
      toast.success(rs.message ?? API_SUCCESS_MESSAGE);
      onClose();
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.GLOBAL.MASTER_COMPANY_SPECIFIC_INDUSTRY] });
    },
    onError: (error) => {
      showError(error);
      setErrorForm(methods, error);
    },
  });

  const onSubmit = (data: TCompanyMainSkillForm) => {
    if (!existingCompanySpecificIndustryId || !existingCompanySpecificBusinessClassificationId) {
      return;
    }
    saveCompanyMainSkill(data);
  };

  return (
    <Modal
      width={860}
      title={selectedCompanyMainSkill ? "編集" : "追加"}
      isOpen={isOpen}
      enableFooter={false}
      onClose={handleRequestClose}
      enableCloseIcon={false}
    >
      <Form methods={methods} onSubmit={onSubmit} schema={companyMainSkillSchema}>
        <Grid container spacing={3} py={3}>
          <Grid item xs={12}>
            <FormField label="業務区分（会社情報用）">
              <Typography variant="body14">{existingCompanySpecificBusinessClassificationName}</Typography>
            </FormField>
          </Grid>
          <Grid item xs={12}>
            <FormField control={control} name="mainSkillName">
              <Input />
            </FormField>
          </Grid>
          <Grid item xs={8}>
            <FormField control={control} name="examName1Year">
              <Input />
            </FormField>
          </Grid>
          <Grid item xs={4}>
            <FormField control={control} name="examGrade1Year">
              <Select options={EXAM_GRADE_1YEAR_OPTIONS} />
            </FormField>
          </Grid>
          <Grid item xs={8}>
            <FormField control={control} name="examNameFinal">
              <Input />
            </FormField>
          </Grid>
          <Grid item xs={4}>
            <FormField control={control} name="examGradeFinal">
              <Select options={EXAM_GRADE_FINAL_OPTIONS} />
            </FormField>
          </Grid>
          <Grid item xs={4}>
            <FormField control={control} name="employerChangeRestrictionPeriod">
              <Select options={EMPLOYER_CHANGE_RESTRICTION_PERIOD_OPTIONS} />
            </FormField>
          </Grid>
        </Grid>
        <Stack flexDirection="row" justifyContent="center" gap="24px">
          <Button variant="outline" onClick={handleRequestClose}>
            キャンセル
          </Button>
          <Button type="submit" disabled={isPending}>
            保存
          </Button>
        </Stack>
      </Form>
      <ConfirmSaveModal open={confirmClose.open} onOk={confirmClose.confirm} onClose={confirmClose.cancel}>
        <Typography>入力したデータがなくなりますが、よろしいですか。</Typography>
      </ConfirmSaveModal>
    </Modal>
  );
};
